import { useTranslation } from "react-i18next";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Flame, ScanLine, Sparkles, Info, CalendarDays, Bell } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { timeUntilQuotaReset } from "@/hooks/useInAppNotifications";
import { loadQuotaPrefs, saveQuotaPrefs, type QuotaNotificationPrefs } from "@/lib/quotaNotificationPrefs";
import { toast } from "sonner";
import { StudyAllowance } from "@/components/StudyAllowance";
import { QuestionCreditStore } from "@/components/QuestionCreditStore";

const TITLE = "AI Questions & Study Streak - Inquira";
const DESC = "Your daily AI question allowance and study streak.";

export const Route = createFileRoute("/usage")({
  component: UsagePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function UsagePage() {
  const { t } = useTranslation();
  const { user, isLoading, isPaid, scanCount, scanLimit, questionUsage, streak, refreshUsage } = useAuth();
  const [lastActive, setLastActive] = useState<string | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [proBonusUntil, setProBonusUntil] = useState<string | null>(null);
  const [weekDays, setWeekDays] = useState<Array<{ label: string; active: boolean }>>([]);
  const [prefs, setPrefs] = useState<QuotaNotificationPrefs | null>(null);

  useEffect(() => { setPrefs(loadQuotaPrefs()); }, []);

  const updatePrefs = (patch: Partial<QuotaNotificationPrefs>) => {
    setPrefs((prev) => {
      const next = { ...(prev ?? loadQuotaPrefs()), ...patch };
      saveQuotaPrefs(next);
      return next;
    });
  };

  useEffect(() => { if (user) refreshUsage(); }, [user, refreshUsage]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("last_active_date, trial_ends_at, pro_bonus_until")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!active) return;
      setLastActive(profile?.last_active_date ?? null);
      setTrialEndsAt(profile?.trial_ends_at ?? null);
      setProBonusUntil(profile?.pro_bonus_until ?? null);

      const since = new Date();
      since.setDate(since.getDate() - 6);
      since.setHours(0, 0, 0, 0);
      const { data: rows } = await supabase
        .from("scan_usage")
        .select("scan_date, scan_count")
        .eq("user_id", user.id)
        .gte("scan_date", since.toISOString().slice(0, 10));
      if (!active) return;
      const used = new Set((rows ?? []).filter((r) => (r.scan_count ?? 0) > 0).map((r) => r.scan_date));
      const days: Array<{ label: string; active: boolean }> = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        days.push({ label: d.toLocaleDateString(undefined, { weekday: "narrow" }), active: used.has(key) });
      }
      setWeekDays(days);
    })();
    return () => { active = false; };
  }, [user]);

  const unlimited = !Number.isFinite(scanLimit) || scanLimit < 0;
  const remaining = unlimited ? Infinity : Math.max(0, scanLimit - scanCount);
  const pct = unlimited ? 100 : Math.min(100, Math.round((scanCount / Math.max(1, scanLimit)) * 100));
  const trialActive = !!trialEndsAt && new Date(trialEndsAt) > new Date();

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Back to solving
      </Link>

      <h1 className="text-2xl font-bold">My AI questions &amp; streak</h1>
      <p className="mt-1 text-sm text-muted-foreground">{DESC}</p>

      {!user ? (
        <div className="mt-6 rounded-xl border border-border p-4 text-sm">
          <p>{t("monetization.sign_in_for_question_balance")}</p>
          <Link to="/login" className="mt-3 inline-block rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-sm">
            Sign in
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <StudyAllowance refreshKey={scanCount} />
          {!questionUsage?.enforcementEnabled && <section className="rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2">
              <ScanLine className="size-4 text-primary" />
              <h2 className="text-sm font-semibold uppercase tracking-wide">{t("monetization.existing_study_allowance")}</h2>
            </div>
            {unlimited ? (
              <>
                <p className="mt-2 text-3xl font-bold">{t("monetization.premium_benefits_preserved")}</p>
                <p className="text-sm text-muted-foreground">
                  {trialActive
                    ? `Your free Pro trial is active until ${trialEndsAt ? new Date(trialEndsAt).toLocaleString() : "the confirmed expiry"}.`
                    : proBonusUntil && new Date(proBonusUntil) > new Date()
                      ? `Pro access granted through ${new Date(proBonusUntil).toLocaleDateString()}.`
                      : "Your existing Premium benefits remain unchanged pending review."}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{t("monetization.legacy_usage_not_question_count")}</p>
              </>
            ) : (
              <>
                <p className="mt-2 text-3xl font-bold">
                  {remaining} <span className="text-base font-medium text-muted-foreground">of {scanLimit} left</span>
                </p>
                <Progress value={pct} className="mt-3" />
                <p className="mt-2 text-xs text-muted-foreground">
                  Used {scanCount} of {scanLimit} today. Your balance resets at midnight UTC.
                </p>
                <p className="mt-1 text-xs font-medium text-primary">
                  Resets in {timeUntilQuotaReset().label}
                </p>
                {remaining === 0 && (
                  <Link to="/upgrade" className="mt-3 inline-flex items-center gap-2 rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-sm font-semibold">
                    <Sparkles className="size-4" /> {t("monetization.compare_premium_study_access")}
                  </Link>
                )}
              </>
            )}
          </section>}

          <section className="rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2">
              <Flame className="size-4 text-orange-500" />
              <h2 className="text-sm font-semibold uppercase tracking-wide">Study streak</h2>
            </div>
            <p className="mt-2 text-3xl font-bold">
              {streak} <span className="text-base font-medium text-muted-foreground">day{streak === 1 ? "" : "s"} in a row</span>
            </p>
            <div className="mt-3 flex items-center gap-1.5">
              {weekDays.map((d, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div
                    className={`size-7 rounded-lg border ${d.active ? "border-orange-500 bg-orange-500/20" : "border-border bg-muted/40"}`}
                    aria-label={d.active ? "Studied" : "No activity"}
                  />
                  <span className="text-[10px] text-muted-foreground">{d.label}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarDays className="size-3.5" />
              {lastActive ? `Last activity: ${new Date(lastActive).toLocaleDateString()}` : "No activity recorded yet."}
            </p>
          </section>

          <section className="rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2">
              <Bell className="size-4 text-primary" />
              <h2 className="text-sm font-semibold uppercase tracking-wide">Low-quota alerts</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Choose when Inquira reminds you that your daily AI questions are running low.
            </p>
            <label className="mt-3 flex items-center justify-between gap-3 text-sm">
              <span>Show low-quota reminders</span>
              <input
                type="checkbox"
                checked={prefs?.enabled ?? true}
                onChange={(e) => updatePrefs({ enabled: e.target.checked })}
                className="size-4 accent-primary"
                data-testid="quota-alerts-enabled"
              />
            </label>
            <label className="mt-3 block text-sm">
              <span className="flex items-center justify-between gap-3">
                Warn me when questions left drop to
                <span className="font-semibold">{prefs?.threshold ?? 1}</span>
              </span>
              <input
                type="range"
                min={0}
                max={Math.max(1, Number.isFinite(scanLimit) ? scanLimit : 3)}
                step={1}
                value={prefs?.threshold ?? 1}
                onChange={(e) => updatePrefs({ threshold: Number(e.target.value) })}
                disabled={!(prefs?.enabled ?? true)}
                className="mt-2 w-full accent-primary"
                data-testid="quota-alerts-threshold"
              />
            </label>
            <label className="mt-3 block text-sm">
              <span className="flex items-center justify-between gap-3">
                Minimum time between reminders
                <span className="font-semibold">{prefs?.cooldownHours ?? 6}h</span>
              </span>
              <input
                type="range"
                min={0}
                max={24}
                step={1}
                value={prefs?.cooldownHours ?? 6}
                onChange={(e) => updatePrefs({ cooldownHours: Number(e.target.value) })}
                disabled={!(prefs?.enabled ?? true)}
                className="mt-2 w-full accent-primary"
                data-testid="quota-alerts-cooldown"
              />
            </label>
            <button
              onClick={() => toast.success("Alert settings saved")}
              className="mt-3 rounded-lg border border-border px-3 py-1.5 text-xs"
            >
              Settings saved automatically
            </button>
          </section>

          <section className="rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2">
              <Info className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold uppercase tracking-wide">How the rules work</h2>
            </div>
            <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
              <li>• <strong className="text-foreground">{t("monetization.free_forever_label")}</strong> {t("monetization.free_question_rule")}</li>
              <li>• <strong className="text-foreground">{t("monetization.image_tutor_label")}</strong> {t("monetization.image_tutor_rule")}</li>
              <li>• <strong className="text-foreground">{t("monetization.follow_up_label")}</strong> {t("monetization.successful_question_rule")}</li>
              <li>• <strong className="text-foreground">{t("monetization.premium_label")}</strong> {t("monetization.premium_question_rule")}</li>
              <li>• <strong className="text-foreground">Daily reset:</strong> {t("monetization.reset_question_rule")}</li>
              <li>• <strong className="text-foreground">Streaks:</strong> solve at least one problem in a day to keep the streak alive. Skipping a full day resets it to 1 on your next study day.</li>
              <li>• <strong className="text-foreground">{t("monetization.premium_trial")}</strong> {t("monetization.the_existing_3_day_introductory_offer_requires_billing_eligibility_a")}</li>
            </ul>
            {!isPaid && (
              <Link to="/upgrade" className="mt-3 inline-flex items-center gap-2 rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-sm font-semibold">
                <Sparkles className="size-4" /> Compare plans
              </Link>
            )}
          </section>
          <QuestionCreditStore />
        </div>
      )}
    </main>
  );
}
